// Pure logic for the MediaSelection "Library" source tab (DL-048).
// No React, no I/O — unit-tested in librarySourceLogic.test.js.
//
// The Library tab lets a builder block REUSE media the user already uploaded
// (surfaced by the standalone Media Library, WL-023) instead of re-uploading.
// It reuses the Media Library's own normalizer/grid/helpers; this module only
// owns the two builder-specific mappings the page doesn't need:
//   1. builder media `type` -> Media Library item `type` (for filtering), and
//   2. a chosen item -> the value shape MediaSelection.handleChange(type, value)
//      already receives from the Upload/Browse/Recent sources.

// Builder passes one of: "image"/"images", "video", "audio", "documents"/"document".
// Media Library items carry singular item.type: "image"/"video"/"audio"/"document".
const BUILDER_TO_ITEM_TYPE = {
  image: "image",
  images: "image",
  video: "video",
  videos: "video",
  audio: "audio",
  audios: "audio",
  document: "document",
  documents: "document",
};

// Map the builder's media type -> the Media Library item type used for filtering.
// Unknown/empty -> null (meaning "no type filter"; show everything).
export function itemTypeForBuilder(builderType) {
  if (!builderType) return null;
  return BUILDER_TO_ITEM_TYPE[String(builderType).toLowerCase()] || null;
}

// Keep only the items whose type matches the block being edited. A null target
// (unknown builder type) passes everything through unchanged.
export function filterByBuilderType(items, builderType) {
  const target = itemTypeForBuilder(builderType);
  if (!target) return items || [];
  return (items || []).filter((it) => it && it.type === target);
}

// Build the value handed to MediaSelection.handleChange(type, value) when a
// Library item is chosen. Mirrors the shape the Upload/Browse/Recent sources
// already emit ({ src, ... }) so every downstream consumer keeps working
// unchanged — src is the canonical URL every consumer reads (e.g. the DMCA
// form stores `.src`; the image/video blocks read `src`/`thumbnail`).
export function toInsertValue(item) {
  if (!item) return null;
  const url = item.url || item.src || "";
  return {
    id: item.id ?? url,
    src: url,
    url,
    title: item.name || item.title || "",
    thumbnail: item.thumbnail || url,
    type: item.type,
  };
}

// ── DL-048 modal: context type-filtering contract ─────────────────────────────
// Gordon addendum 2026-07-14: the reuse modal is mounted at the shared layer and
// inherited by every media-attaching surface, so each call site must declare WHAT
// media types it accepts. We derive that from the consumer's EXISTING `type`
// restriction (image/video/audio/document) — never invent one. `acceptedTypes` is
// an array of item types, or `null`/`[]` meaning "no restriction" (accept all).

// Tab key (helpers.TYPE_TABS) -> the item.type it represents. 'all' -> null.
// Capture ↔ Media Library unify (Phase 1): 'captures' is a SOURCE filter, not a
// type — mapped to null so it behaves like 'all' for tab-visibility (kept in every
// context, since a capture may be any media type; individually-ineligible items
// still render disabled via isItemEligible). It never matches defaultActiveTab's
// single-type lookup (that only compares real types), so it is never auto-opened.
const TAB_KEY_TYPE = {
  all: null,
  images: "image",
  videos: "video",
  audio: "audio",
  documents: "document",
  captures: null,
};

// Plain-language noun per item type, for the ineligible reason line.
const TYPE_NOUN = { image: "Image", video: "Video", audio: "Audio", document: "Document" };

// What item types does a builder context accept? A known builder type restricts
// to exactly that type; an unknown/empty type -> null (no restriction, accept all).
export function acceptedTypesForBuilder(builderType) {
  const t = itemTypeForBuilder(builderType);
  return t ? [t] : null;
}

// Treat null OR an empty array as "no restriction" (fail-open: never lock the
// whole picker out by accident).
function unrestricted(acceptedTypes) {
  return !acceptedTypes || acceptedTypes.length === 0;
}

// Public inverse of `unrestricted` — does this context actually narrow the
// media type at all? Drives whether the picker's "Show all" toggle (2026-07-24
// filter spec) even renders: an unrestricted context has nothing to hide, so
// the toggle would be a no-op and stays off the screen.
export function hasTypeRestriction(acceptedTypes) {
  return !unrestricted(acceptedTypes);
}

// Is a single item selectable in this context? Unrestricted -> always yes.
export function isItemEligible(item, acceptedTypes) {
  if (unrestricted(acceptedTypes)) return true;
  return !!item && acceptedTypes.includes(item.type);
}

// Which type tabs stay visible: always keep 'All'; keep a type tab only when its
// type is accepted. Unrestricted -> every tab. (No dead tabs — addendum.)
export function visibleTypeTabs(tabs, acceptedTypes) {
  if (unrestricted(acceptedTypes)) return tabs || [];
  return (tabs || []).filter((t) => {
    const type = TAB_KEY_TYPE[t.key];
    return type === null || acceptedTypes.includes(type);
  });
}

// Which type tabs the reuse PICKER renders, given the "Show all" toggle state.
// The toggle promises "show all media types, including ones that can't be used
// here" — so while it is ON the type restriction must be lifted at the TAB layer
// too, not only at the item layer. Otherwise the tabs holding the un-hidden media
// (Videos/Audio/Documents) stay off-screen and the toggle reveals nothing at all
// on the single-type tab the picker auto-opens (defaultActiveTab).
export function pickerTypeTabs(tabs, acceptedTypes, showAllTypes) {
  return visibleTypeTabs(tabs, showAllTypes ? null : acceptedTypes);
}

// How many items the default (filtered) view is hiding — the honest number behind
// the "Show all (N hidden)" label and the empty-state copy. Counted across the
// WHOLE (search-scoped) library rather than the open tab, because the tab filter
// hides items too: on an auto-opened single-type tab a per-tab count is always 0
// even when the library is full of media the toggle would reveal.
export function hiddenByTypeCount(items, acceptedTypes) {
  if (unrestricted(acceptedTypes)) return 0;
  return (items || []).filter((it) => !isItemEligible(it, acceptedTypes)).length;
}

// Where to land when "Show all" is switched ON. Stay on the current tab when that
// view will actually have something in it; otherwise fall back to 'All' so the
// toggle never leaves the user staring at an empty grid while the library holds
// media it just un-hid. `itemsOnActiveTab` is the tab+search-filtered count with
// the eligibility filter already lifted (i.e. what ON is about to render).
export function tabOnShowAllOn(activeTab, itemsOnActiveTab) {
  return itemsOnActiveTab > 0 ? activeTab : "all";
}

// Where to land when "Show all" is switched OFF: keep the current tab if it
// survives the restriction, otherwise return to this context's default tab (the
// current tab may be one that only existed while the toggle was ON).
export function tabOnShowAllOff(activeTab, tabs, acceptedTypes) {
  const stillVisible = visibleTypeTabs(tabs, acceptedTypes).some((t) => t.key === activeTab);
  return stillVisible ? activeTab : defaultActiveTab(acceptedTypes);
}

// First tab to show: when exactly one type is accepted, open straight to it so
// the first view is all-eligible; otherwise 'All'.
export function defaultActiveTab(acceptedTypes) {
  if (acceptedTypes && acceptedTypes.length === 1) {
    const only = acceptedTypes[0];
    const key = Object.keys(TAB_KEY_TYPE).find((k) => TAB_KEY_TYPE[k] === only);
    return key || "all";
  }
  return "all";
}

// Plain-language reason an item is not selectable here. `contextLabel` (optional)
// names the surface, e.g. "a wallpaper" -> "Audio files can't be used as a wallpaper".
export function ineligibleReason(item, contextLabel) {
  const noun = TYPE_NOUN[item?.type] || "These";
  const where = contextLabel ? `as ${contextLabel}` : "here";
  return `${noun} files can't be used ${where}`;
}

// Insert guard — enforced in LOGIC, not just the disabled attribute, so an
// ineligible selection can never be inserted.
export function canInsert(item, acceptedTypes) {
  return isItemEligible(item, acceptedTypes);
}
